import React, { useState, useRef } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import PropTypes from 'prop-types';
import axios from 'axios';
import Modal from '../../../commonComponents/modal/Modal';
import List from '../../../commonComponents/lists/List';
import UserColumn from '../../../commonComponents/lists/columns/UserColumn';
import SelectFilter from '../../../commonComponents/lists/filters/SelectFilter';
import TextFilter from '../../../commonComponents/lists/filters/TextFilter';
import UserTypeNamesProp from '../../../commonComponents/propTypes/userTypeNamesProp';
import UserProp from '../../../commonComponents/propTypes/userProp';
import SessionProp from '../propTypes/sessionProp';
import { compose, throttle } from '../../../services/utilityFunctions';
import withCSRF from '../../../commonComponents/withCSRF';
import translate from '../../../commonComponents/translate';
import FormInput from '../../../commonComponents/forms/FormInput';
import Notifications from '../../../services/notifications';
import ParticipationStatus from './Components/ParticipationStatus';

function ParticipantFilterModal(props) {
  const {
    t,
    onClose,
    userTypeNames,
    organizationId,
    session,
    _csrf,
  } = props;

  const optionAny = [{ id: '', text: t('any') }];
  const userTypesFilterList = optionAny.concat([
    { id: UserProp.ROLES.ADMIN, text: t('role_admin') },
    ...Object.keys(userTypeNames).map(userType => ({ id: userType, text: userTypeNames[userType] })),
  ]);

  const tableId = 'userFilterTable';
  const tableEl = useRef(null);
  const [selected, setSelected] = useState([]);
  const [query, setQuery] = useState({
    search: '',
    role: '',
  });
  const [state, setState] = useState({
    participantItem: null,
    email: [],
  });

  // request api to delete participant
  const deleteUser = id => {
    axios.delete(`/api/access-session/users/participants/${session._id}`, {
      data: {
        user: id,
      },
      headers: {
        'Accept-Version': '1.0.0',
        'X-Gamification-API': 'Games',
        'X-CSRF-Token': _csrf,
        _csrf,
      },
    })
      .then(() => {
        Notifications.showNotificationSuccess(
          t('success'),
          t('access_session.detail.remove_participant_success'),
        );
        if (tableEl.current) {
          tableEl.current.forceReload();
        }
      })
      .catch(err => {
        Notifications.showErrorFromRequest(err, 'access_session.detail.remove_participant_error');
        console.log(err);
      });
  };

  const handleChange = field => value =>
    setState(prevState => ({ ...prevState, [field]: value }));

  // request api to add participants and invite
  const addParticipant = (selectedUsers, emails) => {
    const params = {};
    if (selectedUsers) {
      params.users = selectedUsers;
    }
    if (emails) {
      params.emails = emails;
    }
    axios.put(`/api/access-session/users/participants/${session._id}`, params, {
      headers: {
        'Accept-Version': '1.0.0',
        'X-Gamification-API': 'Games',
        'X-CSRF-Token': _csrf,
        _csrf,
      },
    })
      .then(() => {
        Notifications.showNotificationSuccess(
          t('success'),
          t('access_session.detail.add_participant_success'),
        );
        tableEl.current.forceReload();
        setState({
          participantItem: null,
          email: [],
        });
      })
      .catch(err => {
        Notifications.showErrorFromRequest(err, 'access_session.detail.add_participant_error');
        console.log('Err', err);
      });
  };

  function handleListInitialization() {
    $(`#${tableId}`)
      .on('click', '.remove-button', function removeHandler() {
        const id = $(this).attr('data-user-id');
        deleteUser(id);
      });
  }

  const renderUser = _id =>
    renderToStaticMarkup((
      <button
        style={{ textDecoration: 'none' }}
        type="button"
        className="btn btn-link remove-button"
        data-user-id={_id}
      >
        <i className="fa fa-trash-alt" />
      </button>
    ));

  const columns = [
    {
      field: 'name',
      title: t('name'),
      textAlign: 'left',
      width: 80,
      template: user =>
        renderToStaticMarkup(<UserColumn key={user.name} t={t} user={user} includeWrapper={false} />),
    },
    {
      field: 'participationStatus',
      title: t('session.table.participation.participationStatus'),
      textAlign: 'left',
      sortable: false,
      template: user =>
        renderToStaticMarkup(<ParticipationStatus participationStatus={user.participationStatus} t={t} />),
    },
    {
      field: '_id',
      title: '',
      sortable: false,
      textAlign: 'right',
      template: ({ _id }) =>
        renderUser(_id),
    },
  ];

  const handleQueryChange = field => value => {
    setSelected([]);
    setQuery(prev => ({ ...prev, [field]: value }));
  };

  const handleUserTypeChange = value => {
    setSelected([]);
    if (value === '') {
      setQuery(prev => ({ ...prev, type: value, role: value }));
    } else if (value === UserProp.ROLES.ADMIN) {
      setQuery(prev => ({ ...prev, type: '', role: value }));
    } else {
      setQuery(prev => ({ ...prev, type: value, role: UserProp.ROLES.USER }));
    }
  };

  const handleCancel = () => {
    onClose(null);
  };

  const createTag = field => (
    <FormInput
      type="tags"
      id={field}
      name={field}
      onChange={handleChange(field)}
      value={state[field]}
      width="100%"
      placeholder={t(`sessionDetails_userFilterModal.${field}`)}
      inputProps={{
        validationRegex: /^[a-zA-Z0-9+_.-]+@[a-zA-Z0-9.-]+$/,
        onUnselect: item => { console.log('User unselected: ', item); },
      }}
    />
  );

  const handleAddUser = userToAdd => {
    setState(prevState => {
      const participantsList = prevState?.participantItem || [];
      participantsList.push(userToAdd);
      return ({ ...prevState, participantItem: participantsList });
    });
  };

  const handleRemoveUser = userToRemove => {
    setState(prevState => {
      const participantsList = prevState?.participantItem || [];
      return ({ ...prevState, participantItem: participantsList.filter(userId => userId !== userToRemove) });
    });
  };

  const createUser = field => (
    <FormInput
      type="users"
      id={field}
      name={field}
      onChange={handleAddUser}
      value={state[field]}
      width="100%"
      inputProps={{
        multiple: true,
        url: `/academy/non-participants/list/${session._id}`,
        onUnselect: item => handleRemoveUser(item),
        filterUrl: `/academy/non-participants/list/${session._id}`,
      }}
    />
  );

  return (
    <Modal onClose={handleCancel}>
      <div className="modal-dialog modal-lg user-filter">
        <div className="modal-content">
          <div className="container-fluid">
            <div className="modal-header">
              <h5 className="modal-title">{t('sessionDetails_userFilterModal_title')}</h5>
              <button type="button" onClick={handleCancel} className="btn-close">
                <span>x</span>
              </button>
            </div>
            <div className="modal-body">
              <div className="m-form m-form--label-align-right">
                <div className="row align-items-center">
                  <div className="col-lg-9">
                    <div className="form-group m-form__group align-items-center">
                      {createTag('email')}
                    </div>
                  </div>
                  <div className="col-lg-3">
                    <button
                      type="button"
                      style={{ width: '100%' }}
                      className="btn btn-primary "
                      disabled={state?.email?.length === 0}
                      onClick={() => addParticipant(null, state.email)}
                    >
                      {t('sessionDetails_modal.invite')}
                    </button>
                  </div>
                </div>
                <div className="row align-items-center add-participant_container">
                  <div className="col-lg-9">
                    <div className="form-group m-form__group align-items-center">
                      {createUser('participantItem')}
                    </div>
                  </div>
                  <div className="col-lg-3">
                    <button
                      style={{ width: '100%' }}
                      type="button"
                      className="btn btn-primary "
                      disabled={!state.participantItem || state?.participantItem?.length === 0}
                      onClick={() => addParticipant(state.participantItem, null)}
                    >
                      {t('sessionDetails_addParticipant')}
                    </button>
                  </div>
                </div>
                <h5 className="modal-title_participants">
                  {`${t('sessionDetails_modal.participants')}:`}
                </h5>
                <div
                  className="row align-items-center"
                  style={{ marginTop: '20px' }}
                >
                  <div className="col-xl-9 col-md-6 order-1 order-xl-1">
                    <div className="form-group m-form__group align-items-center">
                      <TextFilter
                        onChange={throttle(handleQueryChange('search'), 300)}
                        id="nameFilter"
                        placeholder={t('sessionDetails_modal.searchParticipants')}
                      />
                    </div>
                  </div>
                  <div className="col-xl-3 col-md-6 order-1 order-xl-1">
                    <div className="form-group m-form__group align-items-center">
                      <SelectFilter
                        id="roleFilter"
                        items={userTypesFilterList}
                        onChange={handleUserTypeChange}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <List
                ref={tableEl}
                columns={columns}
                id={tableId}
                dataSourceURL={`/table/access-session/users/list/${session._id}`}
                dataSourceHeaders={{
                  'Accept-Version': '1.0.0',
                  'X-Gamification-API': 'Games',
                }}
                dataSourceParams={{ tenant: organizationId }}
                defaultPageSize={10}
                query={query}
                onInit={handleListInitialization}
              />
            </div>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCancel}
            >
              {t('cancel')}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              onClick={() => onClose(selected)}
            >
              {t('confirm')}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

ParticipantFilterModal.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  organizationId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  userTypeNames: PropTypes.shape(UserTypeNamesProp.propType).isRequired,
  session: PropTypes.shape(SessionProp.propType).isRequired,
};

ParticipantFilterModal.defaultProps = {
};

export default compose(
  translate,
  withCSRF,
)(ParticipantFilterModal);
